import { useEffect, useRef } from 'react';
import { Lock, AlertTriangle, X } from 'lucide-react';

/**
 * FlattenConfirmDialog — modal warning shown before the user flattens overlays.
 *
 * The export converts editable appearances into page content. The current
 * document and its Undo history remain editable.
 */
export default function FlattenConfirmDialog({ open, editCount, annotationCount, formFieldCount = 0, onConfirm, onCancel }) {
  const confirmRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Focus the cancel button by default — flatten is the non-default action
    const t = setTimeout(() => confirmRef.current?.focus(), 50);
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel?.();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onCancel]);

  if (!open) return null;

  const total = (editCount || 0) + (annotationCount || 0) + formFieldCount;
  const itemLabel = total === 1 ? 'item' : 'items';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="flatten-title"
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 mx-4">
        <div className="flex items-start gap-3 mb-4">
          <div className="shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
            <AlertTriangle size={20} className="text-amber-600" />
          </div>
          <div className="flex-1">
            <h2 id="flatten-title" className="text-lg font-semibold text-brand-dark">
              Export a flattened copy?
            </h2>
            <p className="text-sm text-gray-700 mt-1">
              The exported copy will contain {total} {itemLabel} as permanent page content. In that copy:
            </p>
            <ul className="text-sm text-gray-700 mt-2 list-disc pl-5 space-y-1">
              <li>Edits become part of the page — they can't be moved, edited, or removed</li>
              <li>Re-editable data is erased from the file</li>
              <li>Recipients and other PDF viewers see a normal flat document</li>
            </ul>
            <p className="text-sm text-brand-dark font-medium mt-3">
              Your current document stays editable, with its Undo history intact.
            </p>
          </div>
          <button
            onClick={onCancel}
            className="shrink-0 p-1 text-brand-muted hover:text-brand-dark"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex items-center justify-end gap-2 mt-6">
          <button
            ref={confirmRef}
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-brand-dark bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Keep editable
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 flex items-center gap-1.5"
          >
            <Lock size={13} />
            Export flattened copy
          </button>
        </div>
      </div>
    </div>
  );
}
